/**
 * Staff whose incentive is a fixed % of all other staff net incentive
 * (brand allocation + special group), not brand % of section.
 */

export const EMPLOYEE_STAFF_POOL_PCT_PLANS = [
  {
    /** Display / match name (import & employee master often use VINOD.MENJERI). */
    name: "VINOD.MENJERI",
    /** Optional exact employee IDs when known. */
    employeeIds: [],
    ratePct: 5,
  },
];

function normalizePersonKey(v) {
  return String(v ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function getStaffPoolPctPlanForPerson({ name = "", employeeId = "" } = {}) {
  const eid = String(employeeId ?? "").trim();
  const nKey = normalizePersonKey(name);
  for (const plan of EMPLOYEE_STAFF_POOL_PCT_PLANS) {
    const ids = (plan.employeeIds || []).map((x) => String(x).trim()).filter(Boolean);
    if (eid && ids.some((id) => id.toLowerCase() === eid.toLowerCase())) return plan;
    const planKey = normalizePersonKey(plan.name);
    if (planKey && nKey && (nKey === planKey || nKey.includes(planKey) || planKey.includes(nKey))) {
      return plan;
    }
  }
  return null;
}

export function isStaffPoolPctPerson(person, plan = null) {
  const p = plan || getStaffPoolPctPlanForPerson(person);
  return Boolean(p);
}

/**
 * @param {number} otherStaffIncentiveTotal — sum of all other staff finals (excludes this person)
 * @param {number} ratePct — e.g. 5
 */
export function computeStaffPoolPctIncentive(otherStaffIncentiveTotal, ratePct) {
  const base = Math.max(0, Number(otherStaffIncentiveTotal) || 0);
  const rate = Number(ratePct);
  if (!Number.isFinite(rate) || rate <= 0 || base <= 0) return 0;
  return base * (rate / 100);
}

/**
 * Resolve staff-pool % rows for a saved snapshot (VINOD.MENJERI etc.).
 * Uses saved overrides when present; otherwise recomputes from staff/special lines + plans.
 */
export function resolveStaffPoolPctOverridesFromSnapshot(saved) {
  const savedList = Array.isArray(saved?.staffPoolPctOverrides) ? saved.staffPoolPctOverrides : [];
  if (savedList.length) {
    return savedList.map((o) => ({
      name: String(o?.name || "").trim(),
      employeeId: String(o?.employeeId || "").trim(),
      ratePct: Number(o?.ratePct) || 0,
      othersTotal: Number(o?.othersTotal) || 0,
      amount: Number(o?.amount) || 0,
      replacedBrandFinal: Number(o?.replacedBrandFinal) || 0,
      replacedGroupFinal: Number(o?.replacedGroupFinal) || 0,
    })).filter((o) => o.name);
  }

  if (!EMPLOYEE_STAFF_POOL_PCT_PLANS.length) return [];

  const staffLines = saved?.staffLines || [];
  const specialGroupLines = saved?.specialGroupLines || [];
  let allTotal = 0;
  let excluded = 0;
  for (const L of staffLines) {
    const amt = Number(L?.finalAmt) || 0;
    allTotal += amt;
    if (getStaffPoolPctPlanForPerson({ name: L?.name, employeeId: L?.employeeId })) {
      excluded += amt;
    }
  }
  for (const G of specialGroupLines) {
    const amt = Number(G?.incentiveAmount) || 0;
    allTotal += amt;
    if (getStaffPoolPctPlanForPerson({ name: G?.employeeName, employeeId: G?.employeeId })) {
      excluded += amt;
    }
  }
  const othersTotal = Math.max(0, allTotal - excluded);

  return EMPLOYEE_STAFF_POOL_PCT_PLANS.map((plan) => {
    const name = String(plan.name || "").trim();
    if (!name) return null;
    const ratePct = Number(plan.ratePct) || 0;
    return {
      name,
      employeeId: String((plan.employeeIds || [])[0] || "").trim(),
      ratePct,
      othersTotal,
      amount: computeStaffPoolPctIncentive(othersTotal, ratePct),
      replacedBrandFinal: 0,
      replacedGroupFinal: 0,
    };
  }).filter(Boolean);
}
